/** @jsxRuntime classic */
import React from "react";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import { Box, Typography } from "@mui/material";

import WhatsAppBackground from "../../../assets/message-preview/background.png";

function ensureArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function pick(object, keys) {
  if (!object || typeof object !== "object") return "";
  for (const key of keys) {
    if (object[key] !== undefined && object[key] !== null) return object[key];
  }
  return "";
}

function normalizeToken(value) {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function isUsablePreviewUrl(value) {
  const url = String(value || "").trim();
  return /^https?:\/\//i.test(url) && !/\{\{.*\}\}/.test(url);
}

function getMediaKindFromMimeType(mimeType) {
  const value = String(mimeType || "").trim().toLowerCase();
  if (value.startsWith("image/")) return "image";
  if (value.startsWith("video/")) return "video";
  if (value.includes("pdf") || value.includes("document") || value.includes("spreadsheet")) {
    return "document";
  }
  return "";
}

function getMediaKindFromValue(value) {
  const normalized = normalizeToken(value);
  if (normalized.includes("image")) return "image";
  if (normalized.includes("video")) return "video";
  if (normalized.includes("document") || normalized.includes("pdf") || normalized.includes("file")) {
    return "document";
  }
  return "";
}

function getFirstValue(...values) {
  for (const value of values) {
    if (Array.isArray(value)) {
      const nested = getFirstValue(...value);
      if (nested) return nested;
    } else if (value !== undefined && value !== null && String(value).trim()) {
      return value;
    }
  }
  return "";
}

function getMediaCandidateFromField(value, kind) {
  if (!value) return null;
  if (typeof value === "string") {
    return {
      kind,
      url: value,
    };
  }
  if (typeof value !== "object") return null;

  return {
    ...value,
    kind: kind || getMediaKindFromValue(value.kind || value.type || value.format || value.message_type) || getMediaKindFromMimeType(value.mime_type),
    url: getFirstValue(
      value.url,
      value.link,
      value.href,
      value.src,
      value.preview_url,
      value.example_url,
      value.header_handle,
      value.handle,
    ),
  };
}

function getMediaFromHeader(header) {
  if (!header || typeof header !== "object") return null;

  const kind = getMediaKindFromValue(
    header.kind ||
      header.type ||
      header.format ||
      header.media_type ||
      header.message_type,
  );
  if (!kind) return null;

  return getMediaCandidateFromField(header.media || header.asset || header, kind);
}

function getMessageMedia(message) {
  const nestedMessage = getNestedMessage(message);
  const candidates = [
    getMediaCandidateFromField(message?.document || nestedMessage?.document, "document"),
    getMediaCandidateFromField(message?.image || nestedMessage?.image, "image"),
    getMediaCandidateFromField(message?.video || nestedMessage?.video, "video"),
    getMediaCandidateFromField(message?.media || nestedMessage?.media, ""),
    getMediaFromHeader(message?.header || nestedMessage?.header),
  ].filter(Boolean);

  const explicitKind = getMediaKindFromValue(message?.type || nestedMessage?.type);
  if (!candidates.length && explicitKind) {
    return { kind: explicitKind };
  }

  const media = candidates[0];
  if (!media) return null;

  const kind = media.kind || getMediaKindFromMimeType(media.mime_type) || getMediaKindFromValue(media.type) || explicitKind;
  return {
    ...media,
    kind: kind || "document",
  };
}

function getMediaDisplayName(media) {
  const kind = media?.kind || "document";
  const fallback = kind === "image"
    ? "Imagem"
    : kind === "video"
      ? "Vídeo"
      : "Documento";
  return String(
    media?.filename ||
      media?.file_name ||
      media?.name ||
      media?.title ||
      media?.caption ||
      fallback,
  ).trim();
}

function isPdfMedia(media) {
  const mimeType = String(media?.mime_type || media?.mimeType || "").trim().toLowerCase();
  const name = getMediaDisplayName(media).toLowerCase();
  return mimeType.includes("pdf") || name.endsWith(".pdf") || normalizeToken(media?.format).includes("pdf");
}

function getMediaDetail(media) {
  const kind = media?.kind || "document";
  const mimeType = String(media?.mime_type || media?.mimeType || "").trim();
  if (kind === "image") return "Imagem do modelo";
  if (kind === "video") return "Vídeo do modelo";
  if (kind === "document") return "Arquivo do modelo";
  if (mimeType) return mimeType;
  return isPdfMedia(media) ? "PDF" : "Arquivo do modelo";
}

function getTemplateVariableKey(value) {
  const raw = String(value || "").trim();
  const bracket = raw.match(/^\[([^\]]+)\]$/);
  if (bracket) return bracket[1].trim();

  const handlebars = raw.match(/^\{\{\s*([^}]+?)\s*\}\}$/);
  if (handlebars) return handlebars[1].trim();

  const mailchimp = raw.match(/^\*\|(.+?)\|\*$/);
  if (mailchimp) return mailchimp[1].trim();

  return raw;
}

function getNestedMessage(message) {
  return message?.message && typeof message.message === "object"
    ? message.message
    : {};
}

function getMessageButtons(message) {
  const nestedMessage = getNestedMessage(message);
  return [
    ...ensureArray(message?.buttons),
    ...ensureArray(nestedMessage?.buttons),
  ].filter(Boolean);
}

function getMessageFooter(message) {
  const nestedMessage = getNestedMessage(message);
  return String(
    message?.footer ||
      message?.footer_text ||
      nestedMessage?.footer ||
      nestedMessage?.footer_text ||
      "",
  ).trim();
}

function getButtonLabel(button) {
  if (typeof button === "string") return button;
  return String(
    pick(button, ["text", "title", "label", "name", "caption"]) ||
      pick(button?.reply, ["title", "text", "label"]) ||
      pick(button?.action, ["title", "text", "label"]) ||
      "Botao",
  ).trim();
}

function getButtonIcon(button) {
  const type = String(
    button?.type ||
      button?.sub_type ||
      button?.button_type ||
      button?.action?.type ||
      "",
  ).toLowerCase();

  if (type.includes("url") || type.includes("link")) {
    return <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />;
  }
  if (type.includes("phone") || type.includes("call")) {
    return <PhoneRoundedIcon sx={{ fontSize: 16 }} />;
  }
  if (type.includes("quick") || type.includes("reply")) {
    return <ReplyRoundedIcon sx={{ fontSize: 16 }} />;
  }
  return <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />;
}

function TemplateText({ children, muted = false, parameters = {}, showParameterState = false, strong = false }) {
  const parts = String(children || "")
    .split(/(\{\{[^}]+\}\}|\[[^\]]+\]|\*\|[^|]+\|\*)/g)
    .filter(Boolean);

  return (
    <Typography
      component="div"
      sx={{
        color: muted ? "rgb(100, 116, 139)" : "rgb(100, 116, 139)",
        fontSize: 13,
        fontWeight: strong ? 600 : 400,
        lineHeight: "20px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {parts.map((part, index) => {
        const isVariable = /^(\{\{[^}]+\}|\[[^\]]+\]|\*\|[^|]+\|\*)/.test(part);
        if (!isVariable) return <span key={`${part}-${index}`}>{part}</span>;

        const variableKey = getTemplateVariableKey(part);
        const variableValue = String(parameters[variableKey] || "").trim();
        const displayValue = variableValue || part;

        return (
          <Box
            component="span"
            key={`${part}-${index}`}
            sx={{
              color: variableValue
                ? "rgb(37, 99, 235)"
                : showParameterState
                  ? "rgb(239, 68, 68)"
                  : "rgb(79, 70, 229)",
              fontWeight: 500,
            }}
          >
            {displayValue}
          </Box>
        );
      })}
    </Typography>
  );
}

function normalizePreviewChannel(value) {
  return normalizeToken(value);
}

function hasHtmlMarkup(value) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || "").trim());
}

function decodeHtmlEntities(value) {
  const text = String(value || "");

  if (typeof document === "undefined") {
    return text
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
}

function stripHtmlToText(value) {
  return decodeHtmlEntities(
    String(value || "")
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\r/g, "")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n"),
  ).trim();
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function replaceTemplateVariables(value, parameters = {}) {
  return String(value || "").replace(
    /(\{\{[^}]+\}\}|\[[^\]]+\]|\*\|[^|]+\|\*)/g,
    (token) => {
      const variableKey = getTemplateVariableKey(token);
      const variableValue = String(parameters[variableKey] || "").trim();
      return variableValue ? escapeHtml(variableValue) : token;
    },
  );
}

function getEmailMessageBody(message) {
  const nestedMessage = getNestedMessage(message);
  return String(
    message?.content ||
      message?.html ||
      message?.body ||
      message?.text ||
      nestedMessage?.content ||
      nestedMessage?.html ||
      nestedMessage?.body ||
      nestedMessage?.text ||
      "",
  ).trim();
}

function getMessageHeaderImage(message) {
  const nestedMessage = getNestedMessage(message);
  const header = message?.header || nestedMessage?.header;

  if (!header || typeof header !== "object") return "";

  const headerType = normalizeToken(header?.type || header?.format || header?.media_type);
  const candidate = getFirstValue(header?.content, header?.url, header?.src, header?.link);

  if (!candidate || !isUsablePreviewUrl(candidate)) return "";
  if (headerType.includes("img") || headerType.includes("image")) return String(candidate).trim();

  return "";
}

function getEmailPreviewSubject(preview, messages = []) {
  const messageSubject = getFirstValue(
    ...messages.map((message) => {
      const nestedMessage = getNestedMessage(message);
      return getFirstValue(
        message?.subject,
        message?.email_subject,
        nestedMessage?.subject,
        nestedMessage?.email_subject,
      );
    }),
  );

  return String(getFirstValue(preview?.subject, preview?.email_subject, messageSubject)).trim();
}

function getEmailPreviewSubtitle(preview, messages = []) {
  const messageSubtitle = getFirstValue(
    ...messages.map((message) => {
      const nestedMessage = getNestedMessage(message);
      return getFirstValue(
        message?.email_subtitle,
        message?.preheader,
        message?.subtitle,
        nestedMessage?.email_subtitle,
        nestedMessage?.preheader,
        nestedMessage?.subtitle,
      );
    }),
  );

  return String(getFirstValue(
    preview?.email_subtitle,
    preview?.preheader,
    preview?.subtitle,
    messageSubtitle,
  )).trim();
}

function normalizeEmailAccentColor(value, fallback) {
  const color = String(value || "").trim();
  if (
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color) ||
    /^rgba?\([\d\s.,%]+\)$/i.test(color) ||
    /^hsla?\([\d\s.,%]+\)$/i.test(color)
  ) {
    return color;
  }
  return fallback;
}

function getEmailAccentColor(preview, messages = [], fallback) {
  const messageColor = getFirstValue(
    ...messages.map((message) => {
      const nestedMessage = getNestedMessage(message);
      return getFirstValue(message?.email_accent_color, nestedMessage?.email_accent_color);
    }),
  );

  return normalizeEmailAccentColor(
    getFirstValue(preview?.email_accent_color, preview?.accent_color, messageColor),
    fallback,
  );
}

function getEmailSender(preview) {
  return String(getFirstValue(
    preview?.from,
    preview?.sender,
    preview?.sender_name,
    preview?.channel_instance_label,
    preview?.channel_identifier,
  )).trim();
}

function getEmailHeaderTextColor(theme, accentColor) {
  if (typeof theme.palette.getContrastText === "function") {
    return theme.palette.getContrastText(accentColor);
  }
  return theme.palette.primary.contrastText;
}

function getEmailIframeDocument(content, parameters) {
  const replacedContent = replaceTemplateVariables(content, parameters);
  if (/(<!doctype|<html[\s>])/i.test(replacedContent)) return replacedContent;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        box-sizing: border-box;
        color: #1f2a44;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 14px;
        line-height: 1.6;
        margin: 0;
        padding: 24px;
        word-break: break-word;
      }
      img {
        height: auto;
        max-width: 100%;
      }
      table {
        max-width: 100%;
      }
    </style>
  </head>
  <body>${replacedContent}</body>
</html>`;
}

function EmailActionButton({ accentColor, button }) {
  return (
    <Box
      sx={{
        alignItems: "center",
        border: `1px solid ${accentColor}`,
        borderRadius: 999,
        color: accentColor,
        display: "inline-flex",
        fontSize: 13,
        fontWeight: 700,
        justifyContent: "center",
        lineHeight: "18px",
        minHeight: 34,
        px: 1.5,
      }}
    >
      {getButtonLabel(button)}
    </Box>
  );
}

function EmailBodyPreview({ accentColor, bodyText, contentIsHtml, messages = [], parameters, showParameterState }) {
  const attachments = messages
    .map(getMessageMedia)
    .filter(Boolean)
    .filter((media) => media?.kind !== "image" || !messages.some((message) => {
      const mediaUrl = getFirstValue(media?.url, media?.link, media?.href, media?.src, media?.preview_url);
      return getMessageHeaderImage(message) === mediaUrl;
    }));
  const buttons = messages.flatMap(getMessageButtons);
  const footers = messages.map(getMessageFooter).filter(Boolean);
  const hasAfterContent = Boolean(attachments.length || buttons.length || footers.length);

  if (contentIsHtml) {
    return (
      <Box>
        <Box
          component="iframe"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={getEmailIframeDocument(bodyText, parameters)}
          title="Prévia HTML do e-mail"
          sx={{
            backgroundColor: "background.paper",
            border: 0,
            display: "block",
            minHeight: 300,
            width: "100%",
          }}
        />
        {hasAfterContent ? (
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
            {attachments.length ? (
              <Box sx={{ mt: 2 }}>
                {attachments.map((media, index) => (
                  <TemplateMediaAttachment
                    key={`${getMediaDisplayName(media)}-${index}`}
                    media={media}
                  />
                ))}
              </Box>
            ) : null}

            {buttons.length ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {buttons.map((button, index) => (
                  <EmailActionButton
                    accentColor={accentColor}
                    button={button}
                    key={`${getButtonLabel(button)}-${index}`}
                  />
                ))}
              </Box>
            ) : null}

            {footers.length ? (
              <Box sx={{ mt: 2 }}>
                <TemplateText muted parameters={parameters} showParameterState={showParameterState}>
                  {footers.join("\n")}
                </TemplateText>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <TemplateText parameters={parameters} showParameterState={showParameterState}>
        {bodyText || "Prévia indisponível para este modelo."}
      </TemplateText>

      {attachments.length ? (
        <Box sx={{ mt: 2 }}>
          {attachments.map((media, index) => (
            <TemplateMediaAttachment
              key={`${getMediaDisplayName(media)}-${index}`}
              media={media}
            />
          ))}
        </Box>
      ) : null}

      {buttons.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
          {buttons.map((button, index) => (
            <EmailActionButton
              accentColor={accentColor}
              button={button}
              key={`${getButtonLabel(button)}-${index}`}
            />
          ))}
        </Box>
      ) : null}

      {footers.length ? (
        <Box sx={{ mt: 2 }}>
          <TemplateText muted parameters={parameters} showParameterState={showParameterState}>
            {footers.join("\n")}
          </TemplateText>
        </Box>
      ) : null}
    </Box>
  );
}

function EmailPreviewSurface({ messages = [], parameters, preview, showParameterState }) {
  const bodyText = messages
    .map(getEmailMessageBody)
    .filter(Boolean)
    .join("\n\n");
  const contentIsHtml = hasHtmlMarkup(bodyText);
  const displayBody = contentIsHtml ? bodyText : stripHtmlToText(bodyText);
  const subject = getEmailPreviewSubject(preview, messages);
  const subtitle = getEmailPreviewSubtitle(preview, messages);
  const sender = getEmailSender(preview);
  const headerImage = getFirstValue(...messages.map(getMessageHeaderImage));

  return (
    <Box
      sx={(theme) => {
        const isDark = theme.palette.mode === "dark";

        return {
          backgroundColor: isDark ? theme.palette.surface.default : theme.palette.surface.subtle,
          border: `1px solid ${theme.palette.border.default}`,
          borderRadius: "8px",
          boxSizing: "border-box",
          minHeight: "100%",
          p: { xs: 1.25, sm: 1.5 },
        };
      }}
    >
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          margin: "0 auto",
          maxWidth: 560,
          minHeight: "100%",
          width: "100%",
        }}
      >
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.border.default}`,
            borderRadius: "8px",
            overflow: "hidden",
          })}
        >
          {(subject || sender) ? (
            <Box
              sx={(theme) => ({
                borderBottom: `1px solid ${theme.palette.border.default}`,
                px: { xs: 2, sm: 3 },
                py: 1.5,
              })}
            >
              {subject ? (
                <Typography
                  sx={{
                    color: "text.primary",
                    fontSize: 15,
                    fontWeight: 700,
                    lineHeight: "22px",
                    overflowWrap: "anywhere",
                  }}
                >
                  {subject}
                </Typography>
              ) : null}
              {sender ? (
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: "18px",
                    mt: subject ? 0.25 : 0,
                    overflowWrap: "anywhere",
                  }}
                >
                  De: {sender}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          <Box
            sx={(theme) => {
              const accentColor = getEmailAccentColor(preview, messages, theme.palette.primary.main);

              return {
                backgroundColor: theme.palette.background.paper,
                borderTop: subject || sender ? 0 : `4px solid ${accentColor}`,
              };
            }}
          >
            {(headerImage || subtitle) ? (
              <Box
                sx={(theme) => {
                  const accentColor = getEmailAccentColor(preview, messages, theme.palette.primary.main);

                  return {
                    backgroundColor: accentColor,
                    color: getEmailHeaderTextColor(theme, accentColor),
                    p: { xs: 2, sm: 3 },
                    textAlign: "center",
                  };
                }}
              >
                {headerImage ? (
                  <Box
                    alt="Imagem de cabeçalho do e-mail"
                    component="img"
                    src={headerImage}
                    sx={{
                      borderRadius: "8px",
                      display: "block",
                      height: "auto",
                      margin: "0 auto",
                      maxWidth: 220,
                      width: "100%",
                    }}
                  />
                ) : null}
                {subtitle ? (
                  <Typography
                    sx={{
                      color: "inherit",
                      fontSize: { xs: 20, sm: 24 },
                      fontWeight: 700,
                      lineHeight: { xs: "26px", sm: "31px" },
                      mt: headerImage ? 2 : 0,
                      overflowWrap: "anywhere",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            <EmailBodyPreview
              accentColor={getEmailAccentColor(preview, messages, "rgb(124, 58, 237)")}
              bodyText={displayBody}
              contentIsHtml={contentIsHtml}
              messages={messages}
              parameters={parameters}
              showParameterState={showParameterState}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function TemplateMediaAttachment({ media }) {
  const name = getMediaDisplayName(media);
  const detail = getMediaDetail(media);
  const previewUrl = getFirstValue(media?.url, media?.link, media?.href, media?.src, media?.preview_url);
  const canPreviewImage = media?.kind === "image" && isUsablePreviewUrl(previewUrl);

  if (canPreviewImage) {
    return (
      <Box
        sx={{
          backgroundColor: "rgb(248, 250, 252)",
          borderRadius: "7px",
          mb: 1,
          overflow: "hidden",
        }}
      >
        <Box
          alt={name || "Imagem do modelo"}
          component="img"
          src={previewUrl}
          sx={{
            display: "block",
            maxHeight: 190,
            objectFit: "cover",
            width: "100%",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        alignItems: "center",
        backgroundColor: "rgb(248, 250, 252)",
        borderRadius: "8px",
        display: "flex",
        gap: 1,
        mb: 1,
        minHeight: 51,
        minWidth: 0,
        px: 1,
        py: 1,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: "rgb(237, 233, 254)",
          borderRadius: "8px",
          color: media?.kind === "video" ? "rgb(79, 70, 229)" : "rgb(124, 58, 237)",
          display: "flex",
          flexShrink: 0,
          height: 34,
          justifyContent: "center",
          width: 34,
        }}
      >
        {media?.kind === "image" ? (
          <ImageRoundedIcon sx={{ fontSize: 20 }} />
        ) : media?.kind === "video" ? (
          <VideocamRoundedIcon sx={{ fontSize: 20 }} />
        ) : (
          <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: "rgb(51, 65, 85)",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: "20px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typography>
        <Typography sx={{ color: "rgb(100, 116, 139)", fontSize: 10, lineHeight: "15px" }} noWrap>
          {detail}
        </Typography>
      </Box>
    </Box>
  );
}

function TemplateButton({ button }) {
  return (
    <Box
      sx={{
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 0.94)",
        borderRadius: "6px",
        color: "rgb(91, 149, 255)",
        display: "flex",
        fontSize: 14,
        fontWeight: 700,
        gap: 0.75,
        justifyContent: "center",
        minHeight: 35,
        px: 1,
      }}
    >
      {getButtonIcon(button)}
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {getButtonLabel(button)}
      </Box>
    </Box>
  );
}

function getMessageHeaderText(message) {
  const nestedMessage = getNestedMessage(message);
  const header = message?.header || nestedMessage?.header;
  if (!header) return "";
  if (typeof header === "string") return header.trim();
  if (typeof header !== "object") return "";
  if (getMediaFromHeader(header)) return "";
  return String(pick(header, ["text", "content", "title", "body"]) || "").trim();
}

function TemplateBubble({ message, parameters, showParameterState }) {
  const nestedMessage = getNestedMessage(message);
  const content = String(message?.content || nestedMessage?.content || "").trim();
  const footer = getMessageFooter(message);
  const buttons = getMessageButtons(message);
  const headerText = getMessageHeaderText(message);
  const media = getMessageMedia(message);
  const hasPreviewContent = Boolean(content || footer || headerText || media);

  return (
    <Box
      sx={{
        alignItems: "stretch",
        display: "flex",
        flexDirection: "column",
        gap: 0.65,
        width: "100%",
      }}
    >
      <Box
        sx={{
          backgroundColor: "common.white",
          borderRadius: "8px",
          p: "8px 16px 12px",
        }}
      >
        {media ? <TemplateMediaAttachment media={media} /> : null}
        {headerText ? (
          <Box sx={{ mb: content ? 0.75 : 0 }}>
            <TemplateText parameters={parameters} showParameterState={showParameterState} strong>{headerText}</TemplateText>
          </Box>
        ) : null}
        {content ? (
          <TemplateText parameters={parameters} showParameterState={showParameterState}>{content}</TemplateText>
        ) : !hasPreviewContent ? (
          <TemplateText muted>Prévia indisponível para este modelo.</TemplateText>
        ) : null}
        {footer ? (
          <Box sx={{ mt: 1 }}>
            <TemplateText muted parameters={parameters} showParameterState={showParameterState}>{footer}</TemplateText>
          </Box>
        ) : null}
      </Box>

      {buttons.map((button, index) => (
        <TemplateButton button={button} key={`${getButtonLabel(button)}-${index}`} />
      ))}
    </Box>
  );
}

export default function TemplateMessagePreview({ parameters = {}, preview, showParameterState = false }) {
  const messages = preview?.messages?.length ? preview.messages : [];
  const channel = normalizePreviewChannel(preview?.channel);
  const isEmailPreview = channel === "email" || channel === "e_mail";

  if (isEmailPreview) {
    return (
      <EmailPreviewSurface
        messages={messages}
        parameters={parameters}
        preview={preview}
        showParameterState={showParameterState}
      />
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "rgb(232, 225, 216)",
        backgroundImage: `url(${WhatsAppBackground})`,
        backgroundPosition: "center",
        backgroundRepeat: "repeat",
        backgroundSize: "cover",
        borderRadius: "8px",
        boxSizing: "border-box",
        minHeight: "100%",
        p: 2,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          gap: 0.7,
          minHeight: "100%",
          py: 0.5,
        }}
      >
        {messages.length ? (
          messages.map((message, index) => (
            <TemplateBubble
              key={message?.id || `message-${index}`}
              message={message}
              parameters={parameters}
              showParameterState={showParameterState}
            />
          ))
        ) : (
          <TemplateBubble message={{ content: "" }} parameters={parameters} showParameterState={showParameterState} />
        )}
      </Box>
    </Box>
  );
}
